import React, { useState, useEffect, useContext, useMemo } from 'react'
import { css } from '@emotion/core'
import { useTranslation } from 'react-i18next'
import { deleteSalesDepartment, fetchSalesDepartment } from '@/apis/aipass'
import { Button } from '@/components/atoms/button'
import { SettingSalesManagerTab } from '@/components/organisms/settings/sales-manager/tab'
import { useErrorHandler } from '@/hooks/use-error-handler'
import { SettingsLayout } from '@/components/layouts/settings-layout'
import { TabContainer } from '@/components/organisms/settings/tab-container'
import { LoaderContextCreator } from '@/contexts/loader'
import { DeleteIcon } from '@/components/molecules/settings/icon/delete-icon'
import { TableBody, TableHeader, TableRow } from '@/components/organisms/settings/sales-manager/table'
import { DepartmentDetailModal } from '@/components/organisms/settings/sales-manager/department-detail-modal'
import { EditFooter } from '@/components/organisms/edit-footer'
import { SalesDepartmentMasterType } from '@/models/sales-manager/sales'
import { useSettingsEditGuard } from '@/hooks/use-settings-edit-guard'

type ContainerProps = {}

export const SettingSalesManagerDepartment: React.FC<ContainerProps> = () => {
  const { t } = useTranslation()
  const { setIsLoading } = useContext(LoaderContextCreator())
  const [salesDepartments, setSalesDepartments] = useState<SalesDepartmentMasterType[]>([])
  const [showDetailProp, setShowDetailProp] = useState<SalesDepartmentMasterType | undefined | null>(undefined)
  const [deleteIds, setDeleteIds] = useState<string[]>([])
  const { errorHandler } = useErrorHandler()
  const { isSettingsReadOnly, guardSettingsEdit } = useSettingsEditGuard()

  const initializeComponent = async (opt?: { noIndicator: boolean }) => {
    !opt?.noIndicator && setIsLoading(true)
    const response = await fetchSalesDepartment()
    setSalesDepartments(response ?? [])
    setDeleteIds([])
    !opt?.noIndicator && setIsLoading(false)
  }

  const addDeleteTarget = (id: string) => {
    if (!guardSettingsEdit()) return
    setDeleteIds([...deleteIds, id])
  }

  const doDelete = async () => {
    if (!guardSettingsEdit()) return
    try {
      setIsLoading(true)
      if (deleteIds.length) {
        const hasRelatedMasterIds = salesDepartments.filter(detail => detail.relatedSubjectMasterCount).map(detail => detail.id)
        const isRelatedDeletion = deleteIds.some(deleteId => hasRelatedMasterIds.includes(deleteId))
        if (isRelatedDeletion) {
          if (!window.confirm(t('There are related subjects.\nDo you really want to delete them?'))) {
            return
          }
        }
        await deleteSalesDepartment(deleteIds)
        await initializeComponent()
      }
    } catch (error) {
      errorHandler(error)
    } finally {
      setIsLoading(false)
    }
  }
  const doDeleteCancel = () => {
    setDeleteIds([])
  }

  const isShowFooter = useMemo(() => deleteIds.length > 0, [deleteIds])
  const deleteFilteredDepartments = useMemo(() => salesDepartments.filter(detail => !deleteIds.includes(detail.id)), [
    salesDepartments,
    deleteIds,
  ])

  useEffect(() => {
    initializeComponent()
  }, [])

  const footerContent = isShowFooter ? <EditFooter onSave={doDelete} onCancel={doDeleteCancel} /> : undefined

  return (
    <SettingsLayout loading={false} footerContent={footerContent}>
      <TabContainer tabComponent={<SettingSalesManagerTab currentTab="Department" />} isShowFooter={isShowFooter}>
        <>
          <div css={containerHeaderStyle}>
            <Button
              buttonType={isSettingsReadOnly ? 5 : 1}
              width={107}
              height={32}
              fontSize={12}
              isDisabled={isSettingsReadOnly}
              onClick={() => {
                if (!guardSettingsEdit()) return
                setShowDetailProp(null)
              }}
              icon={require('@/static/images/plus-white.svg')}
            >
              {t('Add new')}
            </Button>
          </div>
          <TableHeader>
            <div css={codeColStyle}>{t('Department ID')}</div>
            <div css={nameColStyle}>{t('Department name')}</div>
            <div css={deleteColStyle}></div>
          </TableHeader>
          <TableBody>
            {deleteFilteredDepartments.map(detail => (
              <TableRow
                css={css({
                  '> div': { fontWeight: 500 },
                  minHeight: 49,
                })}
                key={detail.id}
                onClick={() => {
                  if (!guardSettingsEdit()) return
                  setShowDetailProp(detail)
                }}
              >
                <div css={[codeColStyle, { marginLeft: 32 }]}>{detail.departmentCode}</div>
                <div css={nameColStyle}>{detail.departmentName}</div>
                <div css={deleteColStyle}>
                  <DeleteIcon onClick={() => addDeleteTarget(detail.id)} />
                </div>
              </TableRow>
            ))}
          </TableBody>
          {showDetailProp !== undefined && (
            <DepartmentDetailModal
              detail={showDetailProp}
              onSave={() => {
                initializeComponent()
                setShowDetailProp(undefined)
              }}
              onCancel={() => setShowDetailProp(undefined)}
            />
          )}
        </>
      </TabContainer>
    </SettingsLayout>
  )
}

const codeColStyle = css({
  marginLeft: 27,
  width: 99,
})
const nameColStyle = css({
  flexGrow: 1,
  overflowWrap: 'anywhere',
  width: 'min-content',
})
const deleteColStyle = css({
  width: 30,
  margin: '0 24px',
})

const containerHeaderStyle = css({
  alignItems: 'center',
  display: 'flex',
  justifyContent: 'space-between',
  padding: '24px 0px 19px 32px',
  borderBottom: '1px solid #F2F2F2',
})
